import type { FC } from 'react';
import { HStack, VStack } from '@chakra-ui/react';
import { printFormat } from 'iban';

import { formatAmount } from 'common/formatAmount';
import { Scope } from 'common/hooks/useAuthenticatedAction/types';
import useErrorHandledMutation from 'common/hooks/useErrorHandledMutation';
import Button from 'components/_core/Button';
import Callout from 'components/_core/Callout';
import SunshineCard from 'components/_core/SunshineCard';
import SunshineTag from 'components/_core/SunshineTag';
import Text from 'components/_core/Text';
import DetailSidebar from 'components/DetailSidebar';
import ErrorCard from 'components/ErrorCard';
import Loader from 'components/Loader';
import SupportingDocumentPreview from 'components/SupportingDocumentPreview';
import useToast from 'components/ToastProvider/useToast';
import TransferInformationRow from 'components/TransferInformationRow';
import { formatDate, PPP } from 'helpers/date';
import { getBankTransferStatusDate } from 'helpers/teamMembersRequests/bankTransfer';

import { RejectRequestDocument } from '../../graphql/rejectRequest.gql';
import { useApproveExpenseClaimRequest } from '../../hooks/useApproveExpenseClaimRequest';
import { useBankTransferRecipientIbanSearch } from '../../hooks/useBankTransferRecipientIbanSearch';
import { useExpenseClaimRequestDetail } from '../../hooks/useExpenseClaimRequestDetail';
import RequestStatusCallout from '../RequestStatusCallout';
import { StrongAuthDetailSidebar } from '../StrongAuthDetailSidebar';
import locales from './locales';

interface ExpenseClaimRequestSidebarProps {
  onClose?: () => void;
  companyProfileId: string;
  requestId: string;
  isOnPendingFilter: boolean;
}

export const ExpenseClaimRequestSidebar: FC<
  ExpenseClaimRequestSidebarProps
> = ({ companyProfileId, isOnPendingFilter, onClose, requestId }) => {
  const { showToast } = useToast();
  const {
    additionalSepaTransferFees,
    error,
    expenseClaimRequest,
    hasExceededQuota,
    loading,
    nextBalance,
  } = useExpenseClaimRequestDetail({
    requestId,
  });
  const {
    bankTransferRecipient,
    error: errorRecipient,
    loading: loadingRecipient,
  } = useBankTransferRecipientIbanSearch(
    expenseClaimRequest?.expenseClaimRequesterPayload.bankTransferRecipient
      .iban,
  );

  const [approveExpenseClaimRequest, { loading: loadingApproval }] =
    useApproveExpenseClaimRequest({
      onCompleted: () => {
        showToast({
          message: locales.requestApproved,
          type: 'success',
        });
        onClose?.();
      },
    });
  const [rejectRequest, { loading: loadingRejectRequest }] =
    useErrorHandledMutation(RejectRequestDocument, {
      onCompleted: () => {
        showToast({
          message: locales.requestRejected,
          type: 'success',
        });
        onClose?.();
      },
    });

  if (error || errorRecipient) {
    return <ErrorCard />;
  }

  if (
    loading ||
    loadingApproval ||
    loadingRejectRequest ||
    loadingRecipient ||
    !expenseClaimRequest
  ) {
    return (
      <DetailSidebar variant="ghost">
        <DetailSidebar.Header />
        <DetailSidebar.Body>
          <Loader />
        </DetailSidebar.Body>
      </DetailSidebar>
    );
  }

  const canMakeTransfer =
    nextBalance >=
    expenseClaimRequest.expenseClaimRequesterPayload.amount.total +
      (hasExceededQuota ? additionalSepaTransferFees : 0);

  const handleApproval = async (token: string) => {
    if (!canMakeTransfer) {
      return;
    }
    await approveExpenseClaimRequest({
      context: {
        headers: {
          'short-lived-token': token,
        },
      },
      variables: {
        input: {
          bankAccountId:
            expenseClaimRequest.expenseClaimRequesterPayload.bankAccountId,
          companyProfileId,
          teamMembersRequestId: requestId,
        },
      },
    });
  };

  const onRefuse = () =>
    rejectRequest({
      variables: {
        input: {
          companyProfileId,
          teamMembersRequestId: expenseClaimRequest.teamMembersRequestId,
        },
      },
    });

  const amount = formatAmount({
    forcePlusSign: false,
    value: expenseClaimRequest.expenseClaimRequesterPayload.amount.total / 100,
    withNoMinorUnitDigits: true,
  });

  const recipient = `${
    bankTransferRecipient?.name ??
    expenseClaimRequest.expenseClaimRequesterPayload.bankTransferRecipient.name
  }
  ${printFormat(
    bankTransferRecipient?.iban ??
      expenseClaimRequest.expenseClaimRequesterPayload.bankTransferRecipient
        .iban,
  )}
    `;

  const employeeName = `${expenseClaimRequest.requester?.firstName ?? ''} ${
    expenseClaimRequest.requester?.lastName ?? ''
  }`;

  const formattedFees = formatAmount({
    forcePlusSign: false,
    value: hasExceededQuota ? additionalSepaTransferFees / 100 : 0,
  });

  return (
    <StrongAuthDetailSidebar
      colorScheme="grey"
      description={locales.strongAuthSidebarDescription}
      onClose={onClose}
      onPasscodeSuccess={handleApproval}
      scope={Scope.TEAM_MEMBERS_REQUESTS_REVIEW}
    >
      {({ showPasscode }) => (
        <>
          <StrongAuthDetailSidebar.Header
            heading={amount}
            iconName="receipt"
            label={locales.expenseClaim}
          />
          <StrongAuthDetailSidebar.Body alignItems="center" paddingX="space-0">
            <SunshineTag
              colorScheme="grey"
              icon="profile"
              justifySelf="flex-end"
              size="sm"
            >
              {employeeName}
            </SunshineTag>
            <VStack
              alignSelf="stretch"
              marginY="space-32"
              paddingX="space-24"
              spacing="space-0"
            >
              {!isOnPendingFilter ? (
                <RequestStatusCallout request={expenseClaimRequest} />
              ) : null}
              <SupportingDocumentPreview
                href={expenseClaimRequest.supportingDocumentsUrls[0] as string}
              />
              <SunshineCard.Group isBoxed={false} marginTop="space-16">
                <TransferInformationRow
                  label={locales.amountLabel}
                  value={amount}
                />
                {expenseClaimRequest?.expenseClaimRequesterPayload.amount.vatItems.map(
                  ({ amount: vatAmount, rate }, i) => (
                    <TransferInformationRow
                      key={i}
                      label={locales.formatString(locales.vatRate, {
                        rate: rate ? Number((rate * 100).toFixed(2)) : 0,
                      })}
                      value={formatAmount({
                        forcePlusSign: false,
                        value: vatAmount / 100,
                        withNoMinorUnitDigits: true,
                      })}
                    />
                  ),
                )}
                <TransferInformationRow
                  label={locales.executionDateLabel[expenseClaimRequest.status]}
                  value={formatDate(
                    getBankTransferStatusDate(expenseClaimRequest),
                    PPP,
                  )}
                />
                <TransferInformationRow
                  label={locales.descriptionLabel}
                  value={
                    expenseClaimRequest?.expenseClaimRequesterPayload
                      .description
                  }
                />
                <TransferInformationRow
                  label={locales.resellerLabel}
                  value={
                    expenseClaimRequest?.expenseClaimRequesterPayload.reseller
                  }
                />
                <TransferInformationRow
                  label={locales.recipientLabel}
                  value={recipient}
                />
              </SunshineCard.Group>
              {isOnPendingFilter && !bankTransferRecipient ? (
                <Callout hideIcon variant="info">
                  <Callout.Title>
                    {locales.newBankTransferRecipient}
                  </Callout.Title>
                </Callout>
              ) : null}
            </VStack>
          </StrongAuthDetailSidebar.Body>
          {isOnPendingFilter ? (
            <StrongAuthDetailSidebar.Footer gap="space-24">
              {hasExceededQuota ? (
                <VStack alignItems="flex-start">
                  <Text fontWeight="weight-500" variant="primary">
                    {locales.exceededQuotaTitle}
                  </Text>
                  <Text>
                    {locales.formatString(locales.exceededQuotaDescription, {
                      fees: formattedFees,
                    })}
                  </Text>
                </VStack>
              ) : null}
              {!canMakeTransfer ? (
                <Callout hideIcon variant="error">
                  <Callout.Content>{locales.insufficientFunds}</Callout.Content>
                </Callout>
              ) : null}
              <HStack spacing="space-24">
                <Button
                  flex={1}
                  isLoading={loadingRejectRequest}
                  onClick={onRefuse}
                  variant="secondary"
                >
                  {locales.refuseRequest}
                </Button>
                <Button
                  flex={1}
                  isDisabled={!canMakeTransfer || loadingRejectRequest}
                  onClick={() => {
                    if (canMakeTransfer) {
                      showPasscode();
                    }
                  }}
                  variant="primary"
                >
                  {locales.approveRequest}
                </Button>
              </HStack>
            </StrongAuthDetailSidebar.Footer>
          ) : null}
        </>
      )}
    </StrongAuthDetailSidebar>
  );
};
